import { canonicalRut } from '@tenders/shared';
import { countryConfig } from '../country';

/**
 * What a company-id FIELD keeps of what was typed, and when it is complete — the
 * one place the forms read `countryConfig.companyIdForm` for that.
 *
 * Digits-only schemes (UZ ИНН 9, KZ БИН/ИИН 12) keep exactly the rule every form
 * had inline: digits only, at most `digits`, complete at exactly `digits`.
 *
 * An alphanumeric scheme (Spain's NIF/CIF/NIE, `companyIdForm.alphanumeric`)
 * keeps `[0-9A-Z]`, upper-cased, drops a leading `ES` VAT prefix («ESB12345674»
 * is how a NIF is often written on invoices), and is complete when it matches
 * the configured shape. The control character is checked by the server only.
 *
 * Chile's RUT (`companyIdForm.format === 'rut'`, Liciora) is written in its
 * canonical spelling as it is typed: digits and a `K`, dots and spaces dropped,
 * a dash before the last character — «760864285» and «76.086.428-5» both show
 * `76086428-5`, «61.704.000-k» shows `61704000-K` (`canonicalRut`).
 */
const FORM = countryConfig.companyIdForm;
const SHAPE = FORM.alphanumeric ? new RegExp(FORM.alphanumeric.pattern) : null;
const RUT = FORM.format === 'rut';

/** A RUT body (8 digits at most) and its check digit, as typed so far. */
const RUT_MAX_CHARS = 9;

/**
 * A RUT field's value: `[0-9K]` upper-cased, leading zeros dropped, a `K` kept
 * only as the check digit, at most eight body digits, and the dash before the
 * last character once there are two. Extra keystrokes past a full RUT are
 * ignored rather than shifting the check digit.
 */
function cleanRutInput(raw: string): string {
  const s = raw.toUpperCase().replace(/[^0-9K]/g, '');
  const last = s.slice(-1);
  const chars = `${s.slice(0, -1).replace(/K/g, '')}${last}`.replace(/^0+/, '');
  const v = chars.slice(0, RUT_MAX_CHARS);
  if (v.length < 2) return v.replace(/K/g, '');
  return `${v.slice(0, -1).replace(/K/g, '')}-${v.slice(-1)}`;
}

/**
 * A RUT written in free text, as a COMPLETE id, in its canonical spelling — or
 * `null`. With a dash (or dots) the writer said where the check digit is, so a
 * seven-digit body counts («7.654.321-K»); without one only a full nine
 * characters (or a final `K`) do, since «76086428» is far more often the first
 * eight digits of a company's `76086428-5` than a whole RUT.
 */
function completeRut(token: string): string | null {
  const s = token.toUpperCase().replace(/[\s.]/g, '');
  const dashed = /^\d{7,8}-[0-9K]$/.test(s);
  if (!dashed && !/^(\d{8}[0-9K]|\d{7}K)$/.test(s)) return null;
  const id = canonicalRut(s);
  return id && SHAPE?.test(id) ? id : null;
}

/** The id's length: digits (UZ, KZ) or characters (ES). */
export const COMPANY_ID_LENGTH = FORM.digits;

/** Whether the id has letters — the field then needs a text keyboard. */
export const COMPANY_ID_ALPHANUMERIC = SHAPE !== null;

/** `inputMode` for the field: the numeric pad unless the id has letters. */
export const COMPANY_ID_INPUT_MODE: 'numeric' | 'text' = SHAPE ? 'text' : 'numeric';

/** The field's value after a keystroke or a paste. */
export function cleanCompanyIdInput(raw: string): string {
  if (!SHAPE) return raw.replace(/\D/g, '').slice(0, FORM.digits);
  if (RUT) return cleanRutInput(raw);
  let v = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (v.length > FORM.digits && v.startsWith('ES')) v = v.slice(2);
  return v.slice(0, FORM.digits);
}

/** Is `value` (already cleaned) a complete id by shape? */
export function isCompleteCompanyId(value: string): boolean {
  if (!SHAPE) return value.length === FORM.digits && /^\d+$/.test(value);
  if (RUT) return SHAPE.test(cleanRutInput(value));
  return SHAPE.test(value);
}

/**
 * The one complete id written somewhere in free text («Construcciones Pérez SL
 * B12345674»), or `''` — alphanumeric schemes only (`null` on UZ/KZ, whose
 * callers keep their digit-run rule). Two candidates are as good as none:
 * guessing which is the id would submit the wrong company.
 */
export function companyIdInText(text: string): string | null {
  if (!SHAPE) return null;
  if (RUT) {
    const whole = completeRut(text);
    if (whole) return whole;
    const found = new Set(
      text
        .split(/[^0-9A-Za-z.-]+/)
        .map((tok) => completeRut(tok.replace(/^[.-]+|[.-]+$/g, '')))
        .filter((id): id is string => id !== null),
    );
    return found.size === 1 ? [...found][0]! : '';
  }
  const whole = cleanCompanyIdInput(text.replace(/[\s.-]+/g, ''));
  if (text.replace(/[\s.-]+/g, '').length <= FORM.digits + 2 && SHAPE.test(whole)) return whole;
  const found = text
    .toUpperCase()
    .split(/[^0-9A-Z]+/)
    .map((tok) => (tok.length === FORM.digits + 2 && tok.startsWith('ES') ? tok.slice(2) : tok))
    .filter((tok) => SHAPE.test(tok));
  return found.length === 1 ? found[0]! : '';
}

/**
 * What has been typed of an alphanumeric id SO FAR («B123», «b 1234-5»), cleaned
 * like the field, or `null` — alphanumeric schemes only (`null` on UZ/KZ, whose
 * digits are already a prefix). The API reads the same shape as an id prefix
 * (`parseCompanySuggestQuery`): an optional letter, at least two digits, an
 * optional last character, 3 to `digits` characters. Anything else — a name —
 * is `null`.
 */
export function companyIdPrefix(text: string): string | null {
  if (!SHAPE || !/^[\s.\-0-9A-Za-z]+$/.test(text)) return null;
  if (RUT) {
    // A whole RUT is looked up as itself (canonical — the stored spelling has
    // the dash); digits typed so far as a prefix of the stored `digits-DV`.
    const whole = completeRut(text);
    if (whole) return whole;
    const digits = text.replace(/[\s.-]+/g, '');
    return /^\d{3,8}$/.test(digits) ? digits : null;
  }
  let v = text.replace(/[\s.-]+/g, '').toUpperCase();
  if (v.length > FORM.digits && v.startsWith('ES')) v = v.slice(2);
  if (!/^[A-Z]?\d{2,}[0-9A-Z]?$/.test(v)) return null;
  return v.length >= 3 && v.length <= FORM.digits ? v : null;
}
